import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useModule } from '../contexts/ModuleContext';
import { Upload, X, FileText, Check, AlertCircle, Globe, Home } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRAND_DIVISIONS = [
  'Panelart Decor', 'Crown Decor', 'Lamikraft', 'Infinique',
  'Tabillo', 'General / Office', 'Other'
];

const CATEGORIES = [
  'Travel (Flight/Train)', 'Stay (Hotel)', 'Food', 'Local Transport',
  'Client Expense', 'Personal Vehicle', 'Miscellaneous'
];

const PAYMENT_MODES = ['Personal', 'Company Cash', 'Company Card'];

const NewExpense = () => {
  const { user } = useAuth();
  const { activeModule, isDomestic, defaultCurrency, tripType } = useModule();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [billFile, setBillFile] = useState(null);
  const [trips, setTrips] = useState([]);
  const [ledgers, setLedgers] = useState([]);

  const tripIdFromURL = searchParams.get('trip_id');
  const tripNameFromURL = searchParams.get('trip_name');

  const role = user?.role;
  const effectiveModule = role === 'accounts' ? 'INTERNATIONAL' : (role === 'dgm' || role === 'senior_approver') ? 'DOMESTIC' : activeModule;
  const effectiveCurrency = effectiveModule === 'DOMESTIC' ? 'INR' : 'USD';
  const currencyLabel = effectiveCurrency === 'INR' ? 'INR' : effectiveCurrency;

  const [formData, setFormData] = useState({
    department: user?.department || '',
    category: 'Travel (Flight/Train)',
    vendor: '',
    amount: '',
    expense_date: new Date().toISOString().split('T')[0],
    payment_mode: effectiveModule === 'INTERNATIONAL' ? 'Company Card' : 'Personal',
    brand_division: 'General / Office',
    description: '',
    trip_id: tripIdFromURL || '',
    trip_name: tripNameFromURL || '',
    currency: effectiveCurrency,
    ledger_account_type: '',
    kilometres_travelled: ''
  });

  const [perKmRate, setPerKmRate] = useState(10);

  useEffect(() => {
    fetchMyTrips();
    fetchSettings();
    if (effectiveModule === 'INTERNATIONAL') {
      fetchMyLedgers();
    }
  }, [effectiveModule]);

  // Update currency when module changes
  useEffect(() => {
    const newCurrency = effectiveModule === 'DOMESTIC' ? 'INR' : 'USD';
    setFormData(prev => ({
      ...prev,
      currency: newCurrency,
      payment_mode: effectiveModule === 'INTERNATIONAL' ? 'Company Card' : prev.payment_mode,
      ledger_account_type: ''
    }));
  }, [effectiveModule]);

  const fetchMyTrips = async () => {
    try {
      const tripTypeVal = effectiveModule === 'DOMESTIC' ? 'Domestic' : 'International';
      const res = await axios.get(`${API}/trips`, { params: { status: 'ongoing', trip_type: tripTypeVal } });
      setTrips(res.data);
    } catch (err) {
      console.error('Error fetching trips:', err);
    }
  };

  const fetchMyLedgers = async () => {
    try {
      const res = await axios.get(`${API}/ledger`, { params: { user_id: user.id } });
      setLedgers(res.data);
    } catch (err) {
      console.error('Error fetching ledgers:', err);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      if (res.data?.personal_vehicle_rate_per_km) {
        setPerKmRate(res.data.personal_vehicle_rate_per_km);
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  };

  const isPersonalVehicle = formData.category === 'Personal Vehicle';
  const calculatedAmount = isPersonalVehicle && formData.kilometres_travelled
    ? (parseFloat(formData.kilometres_travelled) * perKmRate).toFixed(2)
    : '';

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'trip_id') {
      const selected = trips.find(t => t.id === value);
      setFormData(prev => ({ ...prev, trip_id: value, trip_name: selected?.trip_name || '' }));
    } else if (name === 'ledger_account_type') {
      setFormData(prev => ({ ...prev, ledger_account_type: value }));
    } else if (name === 'category') {
      // Reset km field when category changes
      setFormData(prev => ({
        ...prev,
        category: value,
        kilometres_travelled: '',
        amount: value === 'Personal Vehicle' ? '' : prev.amount
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, bill: 'Please upload a valid file in JPG, PNG, or PDF format' }));
        setBillFile(null);
        e.target.value = '';
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, bill: 'File size must be less than 5MB' }));
        setBillFile(null);
        e.target.value = '';
        return;
      }
      setBillFile(file);
      setErrors(prev => ({ ...prev, bill: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!billFile) newErrors.bill = 'Please upload the bill.';
    if (isPersonalVehicle) {
      if (!formData.kilometres_travelled || parseFloat(formData.kilometres_travelled) <= 0) {
        newErrors.kilometres_travelled = 'Enter valid kilometres travelled.';
      }
    } else {
      if (!formData.amount || parseFloat(formData.amount) <= 0) newErrors.amount = 'Enter a valid amount greater than 0.';
    }
    if (!formData.vendor) newErrors.vendor = 'Vendor name is required.';
    if (!formData.description) newErrors.description = 'Description is required.';
    if (!formData.brand_division) newErrors.brand_division = 'Brand / Division is required.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const submitData = new FormData();
      submitData.append('employee_id', user.id);
      submitData.append('employee_name', user.name);
      submitData.append('department', formData.department || user.department || '');
      submitData.append('category', formData.category);
      submitData.append('vendor', formData.vendor);
      submitData.append('amount', isPersonalVehicle ? calculatedAmount : formData.amount);
      submitData.append('expense_date', formData.expense_date);
      submitData.append('payment_mode', formData.payment_mode);
      submitData.append('brand_division', formData.brand_division);
      submitData.append('description', formData.description);
      submitData.append('expense_type', effectiveModule);
      submitData.append('currency', formData.currency);
      if (isPersonalVehicle && formData.kilometres_travelled) {
        submitData.append('kilometres_travelled', formData.kilometres_travelled);
      }
      if (formData.trip_id) {
        submitData.append('trip_id', formData.trip_id);
        submitData.append('trip_name', formData.trip_name);
      }
      if (effectiveModule === 'INTERNATIONAL' && formData.ledger_account_type) {
        submitData.append('ledger_account_type', formData.ledger_account_type);
      }
      submitData.append('bill', billFile);

      await axios.post(`${API}/expenses`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSuccess(true);
      const redirectTo = formData.trip_id ? `/trips/${formData.trip_id}` : '/expenses/my-requests';
      setTimeout(() => navigate(redirectTo), 1500);
    } catch (err) {
      setErrors(prev => ({ ...prev, submit: err.response?.data?.detail || 'Failed to submit expense.' }));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="p-6 md:p-8">
        <div className="max-w-lg mx-auto">
          <div className="card text-center py-12">
            <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-7 h-7 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Expense Submitted
            </h2>
            <p className="text-sm text-gray-500">
              {effectiveModule === 'DOMESTIC' ? 'Routed for DGM approval' : 'Submitted for Accounts review'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const FieldError = ({ name }) =>
    errors[name] ? (
      <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
        <AlertCircle className="w-3 h-3" />{errors[name]}
      </p>
    ) : null;

  return (
    <div className="p-6 md:p-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Submit New Expense
            </h1>
            <p className="text-sm text-gray-500 mt-1 flex items-center gap-1.5">
              {effectiveModule === 'DOMESTIC' ? (
                <><Home className="w-3.5 h-3.5" /> Domestic expense in {currencyLabel}</>
              ) : (
                <><Globe className="w-3.5 h-3.5" /> International expense in {formData.currency}</>
              )}
            </p>
          </div>
          <div className={`px-3 py-1.5 rounded-full text-xs font-bold ${
            effectiveModule === 'DOMESTIC' ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
          }`} data-testid="expense-module-badge">
            {effectiveModule}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* International Currency & Ledger */}
          {effectiveModule === 'INTERNATIONAL' && (
            <div className="card border-emerald-200 bg-emerald-50/30">
              <h2 className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-4">
                International Expense Settings
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                  <select name="currency" value={formData.currency} onChange={handleInputChange}
                    className="input-field" data-testid="currency-select">
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="GBP">GBP (£)</option>
                    <option value="OTHERS">Others</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deduct From Ledger</label>
                  <select name="ledger_account_type" value={formData.ledger_account_type} onChange={handleInputChange}
                    className="input-field" data-testid="ledger-select">
                    <option value="">No ledger deduction</option>
                    {ledgers.filter(l => l.currency === formData.currency).map(l => (
                      <option key={l.id} value={l.account_type}>
                        {l.account_type} ({l.currency} {Number(l.balance).toLocaleString()})
                      </option>
                    ))}
                  </select>
                  {ledgers.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">No ledger accounts found. Contact Accounts to set up.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Domestic Currency Override */}
          {effectiveModule === 'DOMESTIC' && (
            <div className="card border-indigo-200 bg-indigo-50/30">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                  <select name="currency" value={formData.currency} onChange={handleInputChange}
                    className="input-field" data-testid="domestic-currency-select">
                    <option value="INR">INR (₹)</option>
                    <option value="OTHERS">Others</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Trip Linking */}
          {!tripIdFromURL && trips.length > 0 && (
            <div className="card">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
                Link to {effectiveModule === 'DOMESTIC' ? 'Domestic' : 'International'} Trip (Optional)
              </h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Trip</label>
                <select name="trip_id" value={formData.trip_id} onChange={handleInputChange}
                  className="input-field" data-testid="trip-select">
                  <option value="">Standalone Expense (No Trip)</option>
                  {trips.map(t => (
                    <option key={t.id} value={t.id}>{t.trip_name} ({t.start_date})</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {tripIdFromURL && (
            <div className="card bg-blue-50/50 border-blue-200">
              <p className="text-sm text-blue-700 font-medium">Adding expense to trip: {tripNameFromURL}</p>
            </div>
          )}

          {/* Basic Details */}
          <div className="card">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Basic Details
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Brand / Division <span className="text-red-500">*</span>
                </label>
                <select name="brand_division" value={formData.brand_division}
                  onChange={handleInputChange}
                  className={`input-field ${errors.brand_division ? 'border-red-400' : ''}`}
                  data-testid="brand-division-select"
                >
                  {BRAND_DIVISIONS.map(bd => (
                    <option key={bd} value={bd}>{bd}</option>
                  ))}
                </select>
                <FieldError name="brand_division" />
              </div>
            </div>
          </div>

          {/* Expense Information */}
          <div className="card">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Expense Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                <select name="category" value={formData.category} onChange={handleInputChange}
                  className="input-field" data-testid="category-select">
                  {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vendor *</label>
                <input type="text" name="vendor" value={formData.vendor} onChange={handleInputChange}
                  className={`input-field ${errors.vendor ? 'border-red-400' : ''}`}
                  placeholder="e.g., Uber, Hotel Taj" data-testid="vendor-input" />
                <FieldError name="vendor" />
              </div>

              {/* Personal Vehicle: Kilometres field */}
              {isPersonalVehicle && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Kilometres Travelled *</label>
                    <input type="number" name="kilometres_travelled" value={formData.kilometres_travelled}
                      onChange={handleInputChange}
                      className={`input-field ${errors.kilometres_travelled ? 'border-red-400' : ''}`}
                      placeholder="0" step="0.1" min="0.1" data-testid="km-input" />
                    <FieldError name="kilometres_travelled" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount (Auto-calculated)</label>
                    <div className="input-field bg-gray-50 text-gray-700 font-semibold" data-testid="calculated-amount">
                      {calculatedAmount ? `₹${Number(calculatedAmount).toLocaleString('en-IN')}` : '—'}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Rate: ₹{perKmRate}/km</p>
                  </div>
                </>
              )}

              {/* Regular amount field (hidden for Personal Vehicle) */}
              {!isPersonalVehicle && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount ({formData.currency}) *</label>
                  <input type="number" name="amount" value={formData.amount} onChange={handleInputChange}
                    className={`input-field ${errors.amount ? 'border-red-400' : ''}`}
                    placeholder="0.00" step="0.01" min="0.01" data-testid="amount-input" />
                  <FieldError name="amount" />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expense Date *</label>
                <input type="date" name="expense_date" value={formData.expense_date}
                  onChange={handleInputChange} className="input-field" data-testid="expense-date-input" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode *</label>
                <select name="payment_mode" value={formData.payment_mode} onChange={handleInputChange}
                  className="input-field" data-testid="payment-mode-select">
                  {PAYMENT_MODES.map(mode => <option key={mode} value={mode}>{mode}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea name="description" value={formData.description} onChange={handleInputChange}
                  className={`input-field h-20 resize-none ${errors.description ? 'border-red-400' : ''}`}
                  placeholder="What is this expense for?" data-testid="description-input" />
                <FieldError name="description" />
              </div>
            </div>
          </div>

          {/* Supporting Documents */}
          <div className="card">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Supporting Documents
            </h2>
            {!billFile ? (
              <label className="block cursor-pointer">
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-indigo-400 hover:bg-indigo-50/30 transition-all">
                  <input type="file" onChange={handleFileChange} accept="image/*,.pdf" className="hidden" data-testid="file-input" />
                  <Upload className="w-10 h-10 text-gray-300 mx-auto mb-3" strokeWidth={1.5} />
                  <p className="text-sm font-medium text-gray-600 mb-1">Upload bill or receipt</p>
                  <p className="text-xs text-gray-400">JPG, PNG or PDF (max 5MB)</p>
                </div>
              </label>
            ) : (
              <div className="border border-emerald-200 bg-emerald-50/50 rounded-xl p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="w-8 h-8 text-emerald-600" strokeWidth={1.5} />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{billFile.name}</p>
                      <p className="text-xs text-gray-500">{(billFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                  </div>
                  <button type="button" onClick={() => setBillFile(null)}
                    className="text-gray-400 hover:text-red-500" data-testid="remove-file-button">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
            {errors.bill && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="file-error">
                <p className="text-red-700 text-sm flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.bill}</p>
              </div>
            )}
          </div>

          {/* Approval Flow Info */}
          <div className="card bg-gray-50 border-gray-200">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Approval Flow</h2>
            {effectiveModule === 'DOMESTIC' ? (
              <p className="text-xs text-gray-500">BDM → DGM → Senior Approver → HR (multi-step approval)</p>
            ) : (
              <p className="text-xs text-gray-500">Submitted → Accounts/Admin review</p>
            )}
          </div>

          {/* Actions */}
          {errors.submit && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="submit-error">
              <p className="text-red-700 text-sm flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.submit}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button type="submit" disabled={loading} className="btn btn-primary flex-1 justify-center" data-testid="submit-expense-button">
              {loading ? 'Submitting...' : `Submit ${effectiveModule === 'DOMESTIC' ? 'Domestic' : 'International'} Expense`}
            </button>
            <button type="button" onClick={() => navigate(-1)} className="btn btn-secondary" data-testid="cancel-button">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewExpense;
